/**
 * Perception: vision, hearing, and memory.
 *
 * Shared by granny, the dog, and (later) the rival cat - one framework, five
 * tunings. That reuse is the single biggest cost saving in the project, so
 * resist the urge to write a bespoke sensor for a new character.
 *
 * Line of sight is INJECTED as a callback rather than imported, so this module
 * has no Rapier dependency and the whole perception model is unit-testable with
 * a fake LOS function. tests/unit/sensors.test.ts does exactly that.
 */

import type { Vec3 } from '@/core/math';
import { clamp01, clone, distance, distanceXZ, normalize, sub, vec3 } from '@/core/math';
import { tuning } from '@/core/tuning';

export type LineOfSightFn = (from: Vec3, to: Vec3) => boolean;

export interface VisionResult {
  visible: boolean;
  distance: number;
  /** True when detection came from the 360-degree personal-space bubble. */
  viaPersonalSpace: boolean;
}

/**
 * Can the observer see the target?
 *
 * Three target points are tested (torso, head, paws) rather than one, so a cat
 * peeking out from behind a chair leg is spotted while a fully covered one is
 * not. A single centre-point test produces the infuriating case where the player
 * is visibly exposed but the AI has not noticed.
 *
 * The personal-space bubble is a 360-degree radius: she notices a cat brushing
 * her ankles regardless of which way she is facing. Without it, players learn to
 * stand directly behind her and the illusion collapses.
 */
export function computeVision(params: {
  eye: Vec3;
  yaw: number;
  targetPoints: readonly Vec3[];
  hasLineOfSight: LineOfSightFn;
  fovDeg?: number;
  range?: number;
  personalSpaceRadius?: number;
}): VisionResult {
  const vision = tuning.granny.vision;
  const fovDeg = params.fovDeg ?? vision.fovDeg;
  const range = params.range ?? vision.range;
  const personalSpace = params.personalSpaceRadius ?? vision.personalSpaceRadius;

  const primary = params.targetPoints[0];
  if (!primary) return { visible: false, distance: Infinity, viaPersonalSpace: false };

  const dist = distanceXZ(params.eye, primary);

  // Personal space: ignore facing entirely.
  if (dist <= personalSpace) {
    const visible = params.targetPoints.some((point) => params.hasLineOfSight(params.eye, point));
    if (visible) return { visible: true, distance: dist, viaPersonalSpace: true };
  }

  if (dist > range) return { visible: false, distance: dist, viaPersonalSpace: false };

  // Facing cone. Yaw follows Three's -Z-forward convention.
  const forward = vec3(-Math.sin(params.yaw), 0, -Math.cos(params.yaw));
  const toTarget = normalize(vec3(primary.x - params.eye.x, 0, primary.z - params.eye.z));
  const cosAngle = forward.x * toTarget.x + forward.z * toTarget.z;
  const cosHalfFov = Math.cos((fovDeg * Math.PI) / 360);
  if (cosAngle < cosHalfFov) return { visible: false, distance: dist, viaPersonalSpace: false };

  const visible = params.targetPoints.some((point) => params.hasLineOfSight(params.eye, point));
  return { visible, distance: dist, viaPersonalSpace: false };
}

/**
 * How loud a sound is by the time it reaches the listener.
 *
 * Linear distance falloff then a multiplicative penalty per wall. Deliberately
 * not real acoustics: it is cheap, and more importantly it is LEGIBLE - a player
 * can build an accurate mental model of "two rooms away is safe" in a couple of
 * minutes, which is what stealth needs.
 */
export function perceivedLoudness(params: {
  loudness: number;
  distance: number;
  wallCount: number;
  baseRange?: number;
  perWallAttenuation?: number;
}): number {
  const hearing = tuning.granny.hearing;
  const baseRange = params.baseRange ?? hearing.baseRange;
  const attenuation = params.perWallAttenuation ?? hearing.perWallAttenuation;

  if (params.loudness <= 0) return 0;
  const falloff = clamp01(1 - params.distance / baseRange);
  return params.loudness * falloff * Math.pow(attenuation, params.wallCount);
}

export function canHear(perceived: number, threshold = tuning.granny.hearing.threshold): boolean {
  return perceived >= threshold;
}

/* ------------------------------------------------------------------ */
/* Memory                                                              */
/* ------------------------------------------------------------------ */

export interface SensorMemory {
  /** Where the cat was last seen. Null when she has no idea. */
  lastKnownPosition: Vec3 | null;
  /** Seconds since lastKnownPosition was set. */
  lastKnownAge: number;
  /** Hide spot id -> seconds since she checked it. Stops her looping forever. */
  checkedHideSpots: Map<string, number>;
  /** Where the most recent unexplained noise came from. */
  lastNoisePosition: Vec3 | null;
  lastNoiseLoudness: number;
  /** Seconds since lastNoisePosition was set. */
  lastNoiseAge: number;
}

export function createSensorMemory(): SensorMemory {
  return {
    lastKnownPosition: null,
    lastKnownAge: 0,
    checkedHideSpots: new Map(),
    lastNoisePosition: null,
    lastNoiseLoudness: 0,
    lastNoiseAge: 0,
  };
}

export function rememberSighting(memory: SensorMemory, position: Vec3): void {
  memory.lastKnownPosition = { x: position.x, y: position.y, z: position.z };
  memory.lastKnownAge = 0;
}

export function rememberNoise(memory: SensorMemory, position: Vec3, loudness: number): void {
  // A quieter sound does not overwrite a louder recent one - she investigates the
  // most alarming thing she heard, not merely the latest.
  if (loudness < memory.lastNoiseLoudness * 0.6) return;
  memory.lastNoisePosition = { x: position.x, y: position.y, z: position.z };
  memory.lastNoiseLoudness = loudness;
  memory.lastNoiseAge = 0;
}

export function clearNoiseMemory(memory: SensorMemory): void {
  memory.lastNoisePosition = null;
  memory.lastNoiseLoudness = 0;
  memory.lastNoiseAge = 0;
}

/** Ages memory. Stale sightings are forgotten so she stops staring at old spots. */
export function stepMemory(memory: SensorMemory, dt: number): void {
  if (memory.lastKnownPosition) {
    memory.lastKnownAge += dt;
    if (memory.lastKnownAge > tuning.granny.memory.lastKnownDecaySeconds) {
      memory.lastKnownPosition = null;
      memory.lastKnownAge = 0;
    }
  }

  // Sounds age out too. She hears plenty she does not act on - a calm granny
  // ignores footsteps entirely - and without this the last of them would sit in
  // her memory as a place worth investigating for the rest of the level.
  if (memory.lastNoisePosition) {
    memory.lastNoiseAge += dt;
    if (memory.lastNoiseAge > tuning.granny.memory.noiseDecaySeconds) clearNoiseMemory(memory);
  }

  const recheck = tuning.granny.memory.hideSpotRecheckSeconds;
  for (const [id, age] of memory.checkedHideSpots) {
    const next = age + dt;
    if (next > recheck) memory.checkedHideSpots.delete(id);
    else memory.checkedHideSpots.set(id, next);
  }
}

export function markHideSpotChecked(memory: SensorMemory, hideSpotId: string): void {
  memory.checkedHideSpots.set(hideSpotId, 0);
}

export function hasCheckedHideSpot(memory: SensorMemory, hideSpotId: string): boolean {
  return memory.checkedHideSpots.has(hideSpotId);
}

/**
 * The nearest hide spot she has not already checked. Returns undefined when she
 * has exhausted them all, which is the cue to give up and go back to her routine
 * - and the player's reward for hiding well.
 */
export function nextHideSpotToCheck<T extends { id: string; position: Vec3 }>(
  memory: SensorMemory,
  from: Vec3,
  hideSpots: readonly T[],
): T | undefined {
  let best: T | undefined;
  let bestDist = Infinity;
  for (const spot of hideSpots) {
    if (hasCheckedHideSpot(memory, spot.id)) continue;
    const dist = distance(from, spot.position);
    if (dist < bestDist) {
      bestDist = dist;
      best = spot;
    }
  }
  return best;
}

/** Target points on the cat for LOS tests: torso, head, paws. */
export function catTargetPoints(feet: Vec3, height: number, head?: Vec3): Vec3[] {
  return [
    vec3(feet.x, feet.y + height * 0.5, feet.z), // torso
    head ? clone(head) : vec3(feet.x, feet.y + height * 0.9, feet.z),
    vec3(feet.x, feet.y + 0.04, feet.z), // paws
  ];
}

/** Whether `point` lies inside an axis-aligned box volume. */
export function isInsideVolume(point: Vec3, center: Vec3, size: Vec3): boolean {
  return (
    Math.abs(point.x - center.x) <= size.x / 2 &&
    Math.abs(point.y - center.y) <= size.y / 2 + 0.2 &&
    Math.abs(point.z - center.z) <= size.z / 2
  );
}

/** Direction from a to b, flattened to the ground plane. */
export function flatDirection(from: Vec3, to: Vec3): Vec3 {
  return normalize(vec3(to.x - from.x, 0, to.z - from.z));
}

export { sub };
